import { useEffect, useMemo, useState } from "react";
import { Skeleton } from "@/components/base/Loading";
import { ADMIN_MODULES, DEFAULT_ROLE_PERMISSIONS, ROLE_LABELS, type AdminModule } from "@/lib/adminModules";
import type { StaffMember, UserPermissionRow } from "@/pages/admin/staff/hooks/useStaff";

interface UserPermissionOverrideProps {
  members: StaffMember[];
  userPermissions: UserPermissionRow[];
  loading: boolean;
  onSave: (userId: string, rows: { module: string; allowed: boolean | null }[]) => Promise<number>;
  notify: (text: string, tone?: "success" | "error") => void;
}

type OverrideValue = boolean | null;

interface Draft {
  [key: string]: OverrideValue;
}

type StateKey = "inherit" | "allow" | "deny";

const STATE_OPTIONS: { key: StateKey; label: string; value: OverrideValue }[] = [
  { key: "inherit", label: "跟随角色", value: null },
  { key: "allow", label: "单独放行", value: true },
  { key: "deny", label: "单独收回", value: false },
];

const STATE_LABEL: Record<StateKey, string> = {
  inherit: "跟随角色",
  allow: "单独放行",
  deny: "单独收回",
};

function stateKeyOf(value: OverrideValue): StateKey {
  if (value === true) return "allow";
  if (value === false) return "deny";
  return "inherit";
}

function buildDraft(userPermissions: UserPermissionRow[], userId: string): Draft {
  const draft: Draft = {};
  ADMIN_MODULES.forEach((module) => {
    draft[module.key] = null;
  });
  userPermissions
    .filter((row) => row.user_id === userId)
    .forEach((row) => {
      if (row.module in draft) draft[row.module] = row.allowed;
    });
  return draft;
}

export default function UserPermissionOverride({
  members,
  userPermissions,
  loading,
  onSave,
  notify,
}: UserPermissionOverrideProps) {
  const staffMembers = useMemo(() => members.filter((member) => member.role === "staff"), [members]);
  const [selectedId, setSelectedId] = useState("");
  const [copySourceId, setCopySourceId] = useState("");
  const [draft, setDraft] = useState<Draft>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (staffMembers.length === 0) {
      setSelectedId("");
      return;
    }
    if (!selectedId || !staffMembers.some((member) => member.id === selectedId)) {
      setSelectedId(staffMembers[0].id);
    }
  }, [staffMembers, selectedId]);

  useEffect(() => {
    setDraft(selectedId ? buildDraft(userPermissions, selectedId) : {});
  }, [selectedId, userPermissions]);

  // 可复制的来源：其它客服成员。
  const copySources = useMemo(
    () => staffMembers.filter((member) => member.id !== selectedId),
    [staffMembers, selectedId],
  );

  useEffect(() => {
    if (copySourceId && !copySources.some((member) => member.id === copySourceId)) {
      setCopySourceId("");
    }
  }, [copySources, copySourceId]);

  const selected = staffMembers.find((member) => member.id === selectedId) ?? null;

  const savedDraft = useMemo(
    () => (selectedId ? buildDraft(userPermissions, selectedId) : {}),
    [userPermissions, selectedId],
  );

  const changes = useMemo(
    () =>
      ADMIN_MODULES.map((module) => ({
        module,
        before: stateKeyOf(savedDraft[module.key] ?? null),
        after: stateKeyOf(draft[module.key] ?? null),
      })).filter((item) => item.before !== item.after),
    [savedDraft, draft],
  );

  const dirty = changes.length > 0;

  const overrideCount = useMemo(
    () => ADMIN_MODULES.filter((module) => draft[module.key] !== null).length,
    [draft],
  );

  const setValue = (module: AdminModule, value: OverrideValue) => {
    setDraft((prev) => ({ ...prev, [module]: value }));
  };

  const handleCopy = () => {
    if (!copySourceId) return;
    const source = staffMembers.find((member) => member.id === copySourceId);
    setDraft(buildDraft(userPermissions, copySourceId));
    notify(`已载入 ${source?.full_name || source?.email || "该客服"} 的权限配置，确认后点击「保存覆盖」生效`, "success");
  };

  const handleSave = async () => {
    if (!selectedId) return;
    const rows = ADMIN_MODULES.map((module) => ({ module: module.key, allowed: draft[module.key] ?? null }));
    setSaving(true);
    try {
      const updated = await onSave(selectedId, rows);
      notify(`已更新 ${selected?.full_name || selected?.email || "成员"} 的个人权限（${updated} 项）`, "success");
    } catch (err) {
      notify(err instanceof Error ? err.message : "保存失败", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-base font-semibold text-foreground-950">成员权限覆盖</h2>
          <p className="mt-1 text-xs text-foreground-500">
            在角色级之外，对个别客服单独放行或收回某个模块；「跟随角色」即沿用上方权限矩阵的设置。可一键复制某位客服的配置给另一位，
            调整前会列出变更前后对比。
          </p>
        </div>
        {staffMembers.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={selectedId}
              onChange={(event) => setSelectedId(event.target.value)}
              className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            >
              {staffMembers.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.full_name || member.email || member.id.slice(0, 8)}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleSave}
              disabled={!dirty || saving || loading}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-user-settings-line" />
              </span>
              {saving ? "保存中…" : dirty ? "保存覆盖" : "已保存"}
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="mt-5 space-y-3">
          {[0, 1, 2].map((item) => (
            <Skeleton key={item} className="h-11 w-full" />
          ))}
        </div>
      ) : staffMembers.length === 0 ? (
        <div className="mt-5 flex flex-col items-center gap-2 rounded-lg border border-dashed border-background-300 py-12 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-user-shared-line text-xl" />
          </span>
          <p className="text-sm text-foreground-600">暂无客服成员，先把某位成员调整为「客服」再来设置个人覆盖</p>
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-foreground-600">
            <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-secondary-900">
              当前成员：{selected ? ROLE_LABELS[selected.role] ?? selected.role : "—"}
            </span>
            {overrideCount > 0 && (
              <span className="rounded-full bg-accent-100 px-2.5 py-1 text-accent-700">
                已设 {overrideCount} 项个人覆盖
              </span>
            )}
          </div>

          {copySources.length > 0 && (
            <div className="mt-3 flex flex-col gap-2 rounded-md border border-background-200 bg-background-100 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-xs text-foreground-600">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-file-copy-2-line" />
                </span>
                一键复制某位客服的权限配置给当前成员
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={copySourceId}
                  onChange={(event) => setCopySourceId(event.target.value)}
                  className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                >
                  <option value="">选择来源客服</option>
                  {copySources.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.full_name || member.email || member.id.slice(0, 8)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleCopy}
                  disabled={!copySourceId}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-3.5 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-down-double-line" />
                  </span>
                  复制配置
                </button>
              </div>
            </div>
          )}

          {dirty && (
            <div className="mt-3 rounded-md border border-accent-200 bg-accent-50 p-3">
              <div className="flex items-center gap-2 text-xs font-medium text-accent-700">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-left-right-line" />
                </span>
                变更前后对比（{changes.length} 项待保存）
              </div>
              <ul className="mt-2 space-y-1.5">
                {changes.map((item) => (
                  <li key={item.module.key} className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-medium text-foreground-900">{item.module.label}</span>
                    <span className="rounded-full bg-background-200 px-2 py-0.5 text-foreground-700">
                      {STATE_LABEL[item.before]}
                    </span>
                    <span className="flex h-3 w-3 items-center justify-center text-foreground-400">
                      <i className="ri-arrow-right-line" />
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 font-medium ${
                        item.after === "allow"
                          ? "bg-primary-100 text-primary-700"
                          : item.after === "deny"
                            ? "bg-accent-100 text-accent-700"
                            : "bg-background-200 text-foreground-700"
                      }`}
                    >
                      {STATE_LABEL[item.after]}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-3 overflow-hidden rounded-lg border border-background-200">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-background-100 text-left text-xs text-foreground-600">
                  <th className="px-4 py-3 font-medium">模块</th>
                  <th className="hidden px-4 py-3 font-medium md:table-cell">角色默认</th>
                  <th className="px-4 py-3 font-medium">个人覆盖</th>
                </tr>
              </thead>
              <tbody>
                {ADMIN_MODULES.map((module) => {
                  const roleDefault = DEFAULT_ROLE_PERMISSIONS.staff[module.key];
                  const current = stateKeyOf(draft[module.key] ?? null);
                  const changed = stateKeyOf(savedDraft[module.key] ?? null) !== current;
                  return (
                    <tr
                      key={module.key}
                      className={`border-t border-background-200 align-top ${changed ? "bg-accent-50/60" : ""}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-3">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                            <i className={`${module.icon} text-base`} />
                          </span>
                          <div>
                            <p className="font-medium text-foreground-950">{module.label}</p>
                            <p className="mt-0.5 text-xs text-foreground-500">{module.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="hidden px-4 py-3 md:table-cell">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                            roleDefault ? "bg-primary-100 text-primary-700" : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          {roleDefault ? "可访问" : "不可访问"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="inline-flex rounded-full border border-background-300 bg-background-100 p-1">
                          {STATE_OPTIONS.map((option) => {
                            const active = current === option.key;
                            const tone =
                              option.key === "allow"
                                ? "bg-primary-500 text-background-50"
                                : option.key === "deny"
                                  ? "bg-accent-500 text-background-50"
                                  : "bg-background-50 text-foreground-800";
                            return (
                              <button
                                key={option.key}
                                type="button"
                                onClick={() => setValue(module.key, option.value)}
                                className={`inline-flex cursor-pointer items-center whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                                  active ? tone : "text-foreground-600 hover:text-foreground-900"
                                }`}
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}